import * as React from 'react'
import { useState, useEffect } from 'react'
import { ProductCard } from './ProductCard'
import AdditionalGalaDinnerForm from './AdditionalGalaDinnerForm'
import { is } from 'zod/v4/locales'

const ProductSelection = ({
  token,
  url,
  bypassSoldOut,
  sst,
  paidPhysical,
  purchasedPhysical,
  physical,
  purchasedAdditionalGalaDinner,
  purchasedAdditionalGalaDinnerCount = 0,
  additionalGalaDinner,
  maxAdditionalGalaDinnerTickets,
  canAddAccompanyingPerson = false,
  currentAccompanyingPersonsCount = 0,
  maxAccompanyingPersons = 3,
  accompanyingPerson = [],
  isHrdcClaimable = false,
}) => {
  const MAX_TICKETS = maxAdditionalGalaDinnerTickets || 15
  const remainingTickets =
    MAX_TICKETS - (purchasedAdditionalGalaDinnerCount || 0)
  const remainingAccompanyingPersons =
    maxAccompanyingPersons - currentAccompanyingPersonsCount

  const [selected, setSelected] = useState(null)
  const [selectedAdditionalGalaDinner, setSelectedAdditionalGalaDinner] =
    useState(null)
  const [selectedAccompanyingPerson, setSelectedAccompanyingPerson] =
    useState(null)

  // Ticket count (1-remaining) and details map: { 0: {full_name, email, phone, dietary_restriction, dietary_restriction_other}, 1: {...}, ... }
  const [ticketCount, setTicketCount] = useState(1)
  const [ticketDetailsMap, setTicketDetailsMap] = useState({
    0: {
      full_name: '',
      email: '',
      phone: '',
      dietary_restriction: '',
      dietary_restriction_other: '',
    },
  })

  // Accompanying person count (1-3)
  const [accompanyingPersonCount, setAccompanyingPersonCount] = useState(1)

  // Visible products filtering out hidden ones
  const visiblePhysical = (physical || []).filter((p) => !p.hidden)
  const visibleAdditionalGalaDinner = (additionalGalaDinner || []).filter(
    (p) => !p.hidden
  )
  const visibleAccompanyingPerson = (accompanyingPerson || []).filter(
    (p) => !p.hidden
  )

  const hasPhysical = selected !== null || purchasedPhysical

  // Generate product IDs array based on counts
  const additionalGalaDinnerProductIds = selectedAdditionalGalaDinner
    ? Array(ticketCount).fill(selectedAdditionalGalaDinner)
    : []

  const accompanyingPersonProductIds = selectedAccompanyingPerson
    ? Array(accompanyingPersonCount).fill(selectedAccompanyingPerson)
    : []

  const productIds = [
    selected,
    ...additionalGalaDinnerProductIds,
    ...accompanyingPersonProductIds,
  ]
  const disabledSubmitButton = productIds.every((id) => id === null)

  const incrementTicketCount = () => {
    if (ticketCount < remainingTickets) {
      const newCount = ticketCount + 1
      setTicketCount(newCount)
      // Initialize new ticket details
      setTicketDetailsMap({
        ...ticketDetailsMap,
        [newCount - 1]: {
          full_name: '',
          email: '',
          phone: '',
          dietary_restriction: '',
          dietary_restriction_other: '',
        },
      })
    }
  }

  const decrementTicketCount = () => {
    if (ticketCount > 1) {
      const newCount = ticketCount - 1
      setTicketCount(newCount)
      // Remove last ticket details
      const updated = { ...ticketDetailsMap }
      delete updated[newCount]
      setTicketDetailsMap(updated)
    }
  }

  const updateTicketDetails = (index, details) => {
    setTicketDetailsMap({
      ...ticketDetailsMap,
      [index]: details,
    })
  }

  const incrementAccompanyingPersonCount = () => {
    if (accompanyingPersonCount < remainingAccompanyingPersons) {
      setAccompanyingPersonCount(accompanyingPersonCount + 1)
    }
  }

  const decrementAccompanyingPersonCount = () => {
    if (accompanyingPersonCount > 1) {
      setAccompanyingPersonCount(accompanyingPersonCount - 1)
    }
  }

  return (
    <div id="productSelection">
      <form action={url} method="POST">
        <input type="hidden" name="authenticity_token" value={token} />
        <input type="hidden" name="product_ids" value={productIds} />

        {/* Hidden inputs for additional gala dinner tickets */}
        {selectedAdditionalGalaDinner &&
          Array.from({ length: ticketCount }).map((_, index) => {
            const details = ticketDetailsMap[index] || {
              full_name: '',
              email: '',
              phone: '',
              dietary_restriction: '',
              dietary_restriction_other: '',
            }
            return (
              <React.Fragment key={index}>
                <input
                  type="hidden"
                  name={`additional_gala_dinner_user_details[${selectedAdditionalGalaDinner}][${index}][full_name]`}
                  value={details.full_name}
                />
                <input
                  type="hidden"
                  name={`additional_gala_dinner_user_details[${selectedAdditionalGalaDinner}][${index}][email]`}
                  value={details.email}
                />
                <input
                  type="hidden"
                  name={`additional_gala_dinner_user_details[${selectedAdditionalGalaDinner}][${index}][phone]`}
                  value={details.phone}
                />
                <input
                  type="hidden"
                  name={`additional_gala_dinner_user_details[${selectedAdditionalGalaDinner}][${index}][dietary_restriction]`}
                  value={details.dietary_restriction}
                />
                <input
                  type="hidden"
                  name={`additional_gala_dinner_user_details[${selectedAdditionalGalaDinner}][${index}][dietary_restriction_other]`}
                  value={details.dietary_restriction_other || ''}
                />
              </React.Fragment>
            )
          })}

        <ProductCard
          cardTitle={'MACRO 2026'}
          products={visiblePhysical}
          purchased={purchasedPhysical}
          selectedState={selected}
          setSelectedState={setSelected}
          activated={!purchasedPhysical}
          bypassSoldOut={bypassSoldOut}
          handleCheckbox={(e) => {
            if (!e.target.checked) {
              setSelected(null)
              setSelectedAdditionalGalaDinner(null)
              setTicketCount(1)
              setTicketDetailsMap({
                0: {
                  full_name: '',
                  email: '',
                  phone: '',
                  dietary_restriction: '',
                },
              })
            }
          }}
          handleRadio={() => {
            setSelectedAdditionalGalaDinner(null)
            setTicketCount(1)
            setTicketDetailsMap({
              0: {
                full_name: '',
                email: '',
                phone: '',
                dietary_restriction: '',
                dietary_restriction_other: '',
              },
            })
          }}
        />
        <ProductCard
          cardTitle={'Additional Congress Gala Dinner Ticket'}
          products={visibleAdditionalGalaDinner}
          purchased={purchasedAdditionalGalaDinner}
          selectedState={selectedAdditionalGalaDinner}
          setSelectedState={setSelectedAdditionalGalaDinner}
          // if hrdc claimable, only allow if purchasedPhysical, otherwise allow if hasPhysical
          activated={(isHrdcClaimable ? purchasedPhysical : hasPhysical) && !purchasedAdditionalGalaDinner}
          // activated={hasPhysical && !purchasedAdditionalGalaDinner}
          bypassSoldOut={bypassSoldOut}
          handleCheckbox={(e) => {
            if (!e.target.checked) {
              setSelectedAdditionalGalaDinner(null)
              setTicketCount(1)
              setTicketDetailsMap({
                0: {
                  full_name: '',
                  email: '',
                  phone: '',
                  dietary_restriction: '',
                  dietary_restriction_other: '',
                },
              })
            }
          }}
          handleRadio={() => {
            setTicketCount(1)
            setTicketDetailsMap({
              0: {
                full_name: '',
                email: '',
                phone: '',
                dietary_restriction: '',
                dietary_restriction_other: '',
              },
            })
          }}
        />
        {/* HRDC Claimable Disclaimer */}
        { isHrdcClaimable && !purchasedPhysical && (
          <div className="mt-2 text-xs text-center text-red-600">
            <i className="fas fa-info-circle me-2"></i>
            Additional Congress Gala Dinner only available after Main Congress Ticket is purchased.
          </div>
        )}
        {selectedAdditionalGalaDinner &&
          hasPhysical &&
          !purchasedAdditionalGalaDinner && (
            <div className="mb-3 card">
              <div className="card-body">
                {purchasedAdditionalGalaDinnerCount > 0 && (
                  <div className="mb-3 alert alert-info">
                    You have already purchased{' '}
                    {purchasedAdditionalGalaDinnerCount} ticket(s). You can
                    purchase up to {remainingTickets} more ticket(s).
                  </div>
                )}
                <div className="mb-3 d-flex align-items-center justify-content-between">
                  <h6 className="mb-0">Number of Tickets</h6>
                  <div className="gap-3 d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={decrementTicketCount}
                      disabled={ticketCount <= 1}
                    >
                      -
                    </button>
                    <span className="fw-bold">{ticketCount}</span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={incrementTicketCount}
                      disabled={ticketCount >= remainingTickets}
                    >
                      +
                    </button>
                  </div>
                </div>
                <small className="mb-3 text-muted d-block">
                  Selecting {ticketCount} of {remainingTickets} available
                  ticket(s)
                </small>

                {Array.from({ length: ticketCount }).map((_, index) => {
                  const product = visibleAdditionalGalaDinner.find(
                    (p) => p.id === selectedAdditionalGalaDinner
                  )
                  const details = ticketDetailsMap[index] || {
                    full_name: '',
                    email: '',
                    phone: '',
                    dietary_restriction: '',
                    dietary_restriction_other: '',
                  }

                  return (
                    <div key={index} className="pb-3 mb-3 border-bottom">
                      <h6 className="text-primary">
                        Ticket #{index + 1} - {product?.productName}
                      </h6>
                      <AdditionalGalaDinnerForm
                        additionalGalaDinnerUserDetails={details}
                        setAdditionalGalaDinnerUserDetails={(updatedDetails) =>
                          updateTicketDetails(index, updatedDetails)
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          )}

        {/* Accompanying Person */}
        {canAddAccompanyingPerson && accompanyingPerson.length > 0 && (
          <ProductCard
            cardTitle={'International Accompanying Person'}
            products={visibleAccompanyingPerson}
            purchased={currentAccompanyingPersonsCount > 0}
            selectedState={selectedAccompanyingPerson}
            setSelectedState={setSelectedAccompanyingPerson}
            activated={hasPhysical && remainingAccompanyingPersons > 0}
            bypassSoldOut={bypassSoldOut}
            handleCheckbox={(e) => {
              if (!e.target.checked) {
                setSelectedAccompanyingPerson(null)
                setAccompanyingPersonCount(1)
              }
            }}
            handleRadio={() => {
              setAccompanyingPersonCount(1)
            }}
          />
        )}

        {selectedAccompanyingPerson &&
          hasPhysical &&
          canAddAccompanyingPerson &&
          remainingAccompanyingPersons > 0 && (
            <div className="mb-3 card">
              <div className="card-body">
                {currentAccompanyingPersonsCount > 0 && (
                  <div className="mb-3 alert alert-info">
                    You currently have {currentAccompanyingPersonsCount}{' '}
                    accompanying person(s). You can add up to{' '}
                    {remainingAccompanyingPersons} more.
                  </div>
                )}
                <div className="mb-3 d-flex align-items-center justify-content-between">
                  <h6 className="mb-0">Number of Accompanying Persons</h6>
                  <div className="gap-3 d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={decrementAccompanyingPersonCount}
                      disabled={accompanyingPersonCount <= 1}
                    >
                      -
                    </button>
                    <span className="fw-bold">{accompanyingPersonCount}</span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={incrementAccompanyingPersonCount}
                      disabled={
                        accompanyingPersonCount >= remainingAccompanyingPersons
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
                <small className="mb-3 text-muted d-block">
                  Adding {accompanyingPersonCount} of{' '}
                  {remainingAccompanyingPersons} available person(s) (Maximum:{' '}
                  {maxAccompanyingPersons})
                </small>

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  You will be redirected to register the accompanying person
                  details after creating this order.
                </div>
              </div>
            </div>
          )}

        {sst && (
          <strong className="mx-auto d-block">
            All purchases will incur 8% SST
          </strong>
        )}

        <button
          type="submit"
          className="mx-auto mt-4 text-white btn btn-primary d-block"
          disabled={disabledSubmitButton}
        >
          Purchase Pass
        </button>
      </form>
    </div>
  )
}

export default ProductSelection
